import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import AddUtility from './components/AddUtility';
import DeleteUtility from './components/DeleteUtility';
import EditUtility from './components/EditUtility';
import RestoreUtility from './components/RestoreUtility';
import apiClient from '../../../apiClient';
import { defaultOnError, getPaginatedResponse } from '../../../apiClient/utils';
import Spinner from '../../../components/Spinner';
import Switch from '../../../components/Switch';
import { Table, TableCell, TableHead, TablePagination, TableRow, useTablePagination } from '../../../components/Table';
import { Pagination } from '../../../types/TymbeApi';
import { PageTitle } from '../../../components/texts';

const UtilityPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setPageParams, pageStart, pageSize, setPageStart } = useTablePagination();
  const pagination = {
    $skip: pageStart,
    $limit: pageSize,
    $modify: searchParams.get('$modify') ?? undefined,
  };
  const { data, isLoading } = useQuery(
    ['utility', pagination] as [string, Pagination],
    async ({ queryKey: [, page] }) => apiClient.service('utility').find({
      query: {
        ...page,
      },
    }),
    {
      keepPreviousData: true,
      onError: defaultOnError,
    },
  );

  const onSwitchChange = (deleted: boolean) => {
    if (deleted) {
      setSearchParams((oldParams) => {
        oldParams.set('$modify', 'deleted');
        return oldParams;
      });
    } else {
      setSearchParams((oldParams) => {
        oldParams.delete('$modify');
        return oldParams;
      });
    }
    setPageStart(0);
  };

  const utilities = getPaginatedResponse(data);

  return (
    <>
      <div className="flex justify-between p-5 pl-10 gap-4 items-center">
        <PageTitle>Pomůcky</PageTitle>
        <div className='flex gap-5'>
          <Switch onChange={onSwitchChange} checked={!!pagination.$modify} />
          <AddUtility className="self-center" />
        </div>
      </div>
      <Table className="bg-bg table-auto">
        <TableHead>
          <TableRow>
            <TableCell head className="bg-fg-100 max-w-min">ID</TableCell>
            <TableCell head className="bg-fg-100">Jméno</TableCell>
            <TableCell head className="bg-fg-100">Cena</TableCell>
            <TableCell head className="bg-fg-100">Popis</TableCell>
            <TableCell head className="bg-fg-100">Poznámka</TableCell>
            <TableCell head className="bg-fg-100" />
          </TableRow>
        </TableHead>
        <tbody className="relative">
          <Spinner show={isLoading} className="p-1" absolute />
          {!utilities.total && (
            <TableRow><TableCell colSpan={5} className="text-center">Žádné výsledky</TableCell></TableRow>
          )}
          {utilities.data.map((utility) => (
            <TableRow key={utility.id}>
              <TableCell align="right">{utility.id}</TableCell>
              <TableCell>{utility.name}</TableCell>
              <TableCell align="right">{Number(utility.price).toFixed(2)} Kč</TableCell>
              <TableCell>{utility.description}</TableCell>
              <TableCell>{utility.note}</TableCell>
              <TableCell>
                <div className="flex flex-row justify-end gap-1">
                  {utility.deleted_at
                    ? <RestoreUtility id={utility.id} />
                    : <DeleteUtility id={utility.id} />}
                  <EditUtility initialData={utility} />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </Table>

      <TablePagination
        {...setPageParams()}
        rowsCount={utilities.total}
      />
    </>
  );
};

export default UtilityPage;
